import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Button,
  alpha,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Link,
  Divider
} from '@mui/material';
import {
  Login as PunchInIcon,
  Logout as PunchOutIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { getBreakUntilMs, clearBreakSession } from '../../utils/breakSessionStorage';

const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Equal-width chips; kept compact so they stay sequential in the header. */
const HEADER_CHIP_SX = {
  width: 132,
  minHeight: 46,
  px: 0.75,
  py: 0.35,
  borderRadius: 1.25,
  textAlign: 'center',
  lineHeight: 1.1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box'
};

const LABEL_SX = {
  fontSize: '0.5rem',
  fontWeight: 800,
  letterSpacing: 0.15,
  lineHeight: 1.15,
  whiteSpace: 'normal',
  textTransform: 'none'
};

const VALUE_SX = {
  fontSize: '0.85rem',
  fontWeight: 900,
  letterSpacing: 0.15,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  lineHeight: 1.2
};

const DigitalClock = () => {
  const { user } = useAuth();
  const [time, setTime] = useState(new Date());
  const [serverTime, setServerTime] = useState(null);
  const [serverTimezone, setServerTimezone] = useState('');
  const [serverTimezoneIana, setServerTimezoneIana] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punchingIn, setPunchingIn] = useState(false);
  const [punchingOut, setPunchingOut] = useState(false);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  const [todayActivities, setTodayActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const profileTimezone =
    (user?.timezone && String(user.timezone).trim()) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/punchcard/status');
      const data = response.data.data;
      setStatus(data);
      setIdleTimeoutMinutes(data.idleTimeoutMinutes || 10);
      if (data.serverTime) {
        setServerTime(new Date(data.serverTime));
      }
      setServerTimezone(data.serverTimezone || '');
      setServerTimezoneIana(data.serverTimezoneIana || data.serverTimezone || '');
    } catch (error) {
      console.error('Failed to fetch punch status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30 * 1000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handlePunchIn = async () => {
    setPunchingIn(true);
    try {
      await api.post('/punchcard/punch-in');
      clearBreakSession();
      await fetchStatus();
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'punch-in' } }));
      setSnackbar({
        open: true,
        message: 'Punched in! Your work time is now being tracked.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to punch in:', error);
      setSnackbar({
        open: true,
        message: 'Failed to punch in. Please try again.',
        severity: 'error'
      });
    } finally {
      setPunchingIn(false);
    }
  };

  const handlePunchOut = async () => {
    setPunchingOut(true);
    try {
      await api.post('/punchcard/punch-out');
      await fetchStatus();
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'punch-out' } }));
      setSnackbar({
        open: true,
        message: 'Punched out! Have a great rest of your day.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to punch out:', error);
      setSnackbar({
        open: true,
        message: 'Failed to punch out. Please try again.',
        severity: 'error'
      });
    } finally {
      setPunchingOut(false);
    }
  };

  useEffect(() => {
    const handlePunchStatusChange = () => {
      fetchStatus();
    };
    window.addEventListener('punchStatusChanged', handlePunchStatusChange);
    return () => window.removeEventListener('punchStatusChanged', handlePunchStatusChange);
  }, [fetchStatus]);

  const openTodayActivities = async () => {
    setActivitiesOpen(true);
    setActivitiesLoading(true);
    try {
      const day = todayYmd();
      const response = await api.get('/activities/my', {
        params: { page: 1, limit: 100, start_date: day, end_date: day }
      });
      setTodayActivities(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch today activities:', error);
      setTodayActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatClockInZone = (date, timeZone) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timeZone || undefined
      }).format(date);
    } catch {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    }
  };

  const getShortTimezone = (tz) => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(new Date());
      const tzPart = parts.find((p) => p.type === 'timeZoneName');
      return tzPart ? tzPart.value : tz;
    } catch {
      return tz;
    }
  };

  const formatWorkingTimeHhMm = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const isOnline = status?.isOnline;
  const isIdle = status?.isIdle;
  const breakUntilMs = getBreakUntilMs();
  const breakOverdue = Boolean(breakUntilMs && Date.now() > breakUntilMs && isIdle);
  const minutesSinceActivity = status?.minutesSinceLastActivity || 0;
  const totalWorkingMinutes = status?.totalWorkingMinutes || 0;
  const activityCount = status?.todayActivityCount || 0;

  const displayServerTime = serverTime || time;
  const serverTzForFormat = serverTimezoneIana || undefined;
  const serverTzLabel = serverTimezone || (serverTimezoneIana ? getShortTimezone(serverTimezoneIana) : '');
  const localProfileTimeStr = breakOverdue
    ? `+${Math.max(1, Math.ceil((Date.now() - breakUntilMs) / 60000))}m late`
    : `${formatClockInZone(time, profileTimezone)} ${getShortTimezone(profileTimezone)}`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.65,
          py: 0.25,
          px: 0.5,
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.1),
          flexWrap: 'nowrap',
          width: 'max-content',
          maxWidth: '100%'
        }}
      >
        <Tooltip
          title={`Organization server clock (${serverTimezoneIana || serverTzLabel || 'configured timezone'})`}
        >
          <Box
            sx={{
              ...HEADER_CHIP_SX,
              bgcolor: '#BFDBFE',
              color: '#1E3A8A',
              border: '2px solid #3B82F6',
              boxShadow: '0 0 0 1px rgba(59,130,246,0.3), 0 2px 8px rgba(59,130,246,0.25)'
            }}
          >
            <Typography sx={LABEL_SX}>Server Time</Typography>
            <Typography sx={VALUE_SX}>
              {formatClockInZone(displayServerTime, serverTzForFormat)} {serverTzLabel}
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip
          title={
            breakOverdue
              ? `Break ended — punch in. (${Math.max(1, Math.ceil((Date.now() - breakUntilMs) / 60000))} min late)`
              : `Local Profile Time (${profileTimezone}) — from your Profile settings`
          }
        >
          <Box
            sx={{
              ...HEADER_CHIP_SX,
              bgcolor: breakOverdue ? '#FECACA' : '#DDD6FE',
              color: breakOverdue ? '#7F1D1D' : '#4C1D95',
              border: '2px solid',
              borderColor: breakOverdue ? '#EF4444' : '#8B5CF6',
              boxShadow: breakOverdue
                ? '0 0 0 1px rgba(239,68,68,0.35), 0 2px 8px rgba(239,68,68,0.25)'
                : '0 0 0 1px rgba(139,92,246,0.3), 0 2px 8px rgba(139,92,246,0.25)'
            }}
          >
            <Typography sx={LABEL_SX}>Local Profile Time</Typography>
            <Typography sx={{ ...VALUE_SX, fontSize: breakOverdue ? '0.75rem' : '0.85rem' }}>
              {localProfileTimeStr}
            </Typography>
          </Box>
        </Tooltip>

        {isIdle ? (
          <Box
            sx={{
              ...HEADER_CHIP_SX,
              width: 'auto',
              minWidth: 118,
              px: 1,
              bgcolor: '#ECFDF5',
              border: '2px solid #22C55E',
              boxShadow: '0 0 0 1px rgba(34,197,94,0.25)'
            }}
          >
            <Typography sx={{ ...LABEL_SX, color: '#14532D' }}>Status</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={punchingIn ? <CircularProgress size={10} color="inherit" /> : <PunchInIcon sx={{ fontSize: 14 }} />}
              onClick={handlePunchIn}
              disabled={punchingIn}
              sx={{
                bgcolor: '#22c55e',
                '&:hover': { bgcolor: '#16a34a' },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.7rem',
                py: 0.25,
                px: 1,
                minHeight: 26,
                color: 'white',
                mt: 0.25
              }}
            >
              Punch In
            </Button>
          </Box>
        ) : (
          <Tooltip title="You are punched in (online). Use Punch Out when you finish.">
            <Box
              sx={{
                ...HEADER_CHIP_SX,
                width: 'auto',
                minWidth: 158,
                minHeight: 56,
                px: 1.25,
                py: 0.5,
                flexDirection: 'row',
                gap: 1,
                bgcolor: '#ECFDF5',
                border: '2px solid #4ADE80',
                boxShadow:
                  '0 0 0 2px rgba(34,197,94,0.35), 0 0 14px rgba(34,197,94,0.55), 0 2px 8px rgba(34,197,94,0.25)'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: '#22FF66',
                    border: '2.5px solid #000',
                    boxSizing: 'border-box',
                    boxShadow:
                      '0 0 0 1px rgba(255,255,255,0.85), 0 0 8px rgba(34,255,102,0.95), 0 0 14px rgba(34,197,94,0.7)',
                    animation: 'pulseDot 1.4s infinite',
                    '@keyframes pulseDot': {
                      '0%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.82, transform: 'scale(1.1)' },
                      '100%': { opacity: 1, transform: 'scale(1)' }
                    }
                  }}
                />
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 900, color: '#15803D', lineHeight: 1 }}>
                  Online
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={punchingOut ? <CircularProgress size={12} color="inherit" /> : <PunchOutIcon sx={{ fontSize: 16 }} />}
                onClick={handlePunchOut}
                disabled={punchingOut}
                sx={{
                  bgcolor: '#ef4444',
                  '&:hover': { bgcolor: '#dc2626' },
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  py: 0.55,
                  px: 1.35,
                  minHeight: 32,
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.45)'
                }}
              >
                Punch Out
              </Button>
            </Box>
          </Tooltip>
        )}

        <Tooltip title="Your today's working time (hours:minutes)">
          <Box
            sx={{
              ...HEADER_CHIP_SX,
              bgcolor: '#FCD34D',
              color: '#78350F',
              border: '2px solid #F59E0B',
              boxShadow: '0 0 0 1px rgba(245,158,11,0.35), 0 2px 8px rgba(245,158,11,0.35)'
            }}
          >
            <Typography sx={LABEL_SX}>Your Today&apos;s Working Time (hh:mm)</Typography>
            <Typography sx={{ ...VALUE_SX, fontSize: '0.95rem' }}>
              {formatWorkingTimeHhMm(totalWorkingMinutes)}
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip title="Click to view all of today's activities">
          <Box
            component="button"
            type="button"
            onClick={openTodayActivities}
            sx={{
              ...HEADER_CHIP_SX,
              appearance: 'none',
              border: '2px solid #34D399',
              bgcolor: '#A7F3D0',
              color: '#064E3B',
              cursor: 'pointer',
              boxShadow: '0 0 0 1px rgba(16,185,129,0.3), 0 2px 8px rgba(16,185,129,0.25)',
              '&:hover': { bgcolor: '#6EE7B7' }
            }}
          >
            <Typography sx={LABEL_SX}>No Of Activities today</Typography>
            <Typography sx={{ ...VALUE_SX, fontSize: '0.95rem' }}>{activityCount}</Typography>
            <Link
              component="span"
              underline="always"
              sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#065F46', display: 'block', mt: 0.1 }}
            >
              View all
            </Link>
          </Box>
        </Tooltip>

        {isOnline && minutesSinceActivity >= idleTimeoutMinutes - 3 && (
          <Tooltip title={`Will be marked idle in ${Math.max(0, idleTimeoutMinutes - minutesSinceActivity)}m`}>
            <Typography
              variant="caption"
              sx={{
                color: 'warning.main',
                fontWeight: 700,
                fontSize: '0.7rem',
                flexShrink: 0
              }}
            >
              ⚠️
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Dialog open={activitiesOpen} onClose={() => setActivitiesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Your activities today</DialogTitle>
        <DialogContent dividers>
          {activitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : todayActivities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No activities logged today yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {todayActivities.map((a, idx) => (
                <React.Fragment key={a.id || idx}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemText
                      primary={a.description || a.action || 'Activity'}
                      secondary={
                        <>
                          {[a.module, a.entity_name].filter(Boolean).join(' · ')}
                          {a.performed_at
                            ? ` · ${new Date(a.performed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : ''}
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivitiesOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontSize: '0.8rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DigitalClock;
